// ReorderDrawer — the ONE reorder surface of an ordered set (ADR-046): a `StagedDrawer` hosting the `SortableList`,
// the draft seeded from the WHOLE sibling set at each open (a background refetch never clobbers a reorder in
// progress), Save disabled until the order changes, nothing sent until Save — the full ordered set, which the backend
// renumbers 1..N. A refusal is the frame's line (ADR-084 §6): a set that changed under the drawer (a foreign id, a
// count mismatch) says so in its code's words; a busy answer says nothing more than the door's toast. A reorder
// carries no stamp (last-wins curation), so the line offers no Load the latest. The host owns the set and the write: it passes the items, the row's name (and an
// optional trailing face — a status badge, a tier), the three words that name the thing, and the save.
// `useReorderVerb` is the verb that opens it: offered to a manager while two or more stand to be ordered.

import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useResetOnOpen } from '../../hooks/useResetOnOpen.js';
import { Button } from '../ui/Button.js';
import { icons } from '../ui/icons.js';
import { SortableList } from '../ui/SortableList.js';
import type { ListAction } from './ListActions.js';
import { StagedDrawer } from './StagedDrawer.js';
import { useRefusalLine } from './forms/useRefusalLine.js';

export interface ReorderDrawerProps<T> {
  open: boolean;
  onClose: () => void;
  /** The whole sibling set, in display order — never a filtered page (a partial set is refused). */
  items: T[];
  getId: (item: T) => string | number;
  /** The row's name — its face, and the handle's accessible name ("Reorder {{name}}"). */
  getName: (item: T) => string;
  /** What the row shows after its name (a status badge, a tier). */
  trailing?: (item: T) => ReactNode;
  /** The words that name the thing: "Reorder site types", how to move one, the list's accessible name. */
  title: string;
  description: string;
  listLabel: string;
  /** Commit the new order (the whole set). A refusal keeps the drawer open with the frame's line. */
  onSave: (ordered: T[]) => Promise<unknown>;
}

export function ReorderDrawer<T>({ open, onClose, items, getId, getName, trailing, title, description, listLabel, onSave }: ReorderDrawerProps<T>) {
  const { t } = useTranslation('common');
  const [order, setOrder] = useState<T[]>(items);
  const [saving, setSaving] = useState(false);
  const line = useRefusalLine({ edit: false });
  useResetOnOpen(open, (isOpen) => {
    if (isOpen) setOrder(items);
  });

  const dirty = order.length === items.length && order.some((it, i) => getId(it) !== getId(items[i] as T));

  const save = async () => {
    setSaving(true);
    line.dismiss();
    try {
      await onSave(order);
      setSaving(false);
      onClose();
    } catch (err) {
      setSaving(false);
      line.refuse(err);
    }
  };

  return (
    <StagedDrawer
      open={open}
      onClose={onClose}
      title={title}
      description={description}
      dirty={dirty}
      busy={saving}
      line={line}
      footer={(g) => (
        <>
          <Button variant="secondary" onClick={g.requestClose} disabled={saving}>
            {t('actions.cancel')}
          </Button>
          <Button variant="primary" onClick={() => void save()} disabled={!dirty} loading={saving}>
            {t('actions.save')}
          </Button>
        </>
      )}
    >
      <SortableList
        items={order}
        getId={getId}
        onReorder={setOrder}
        ariaLabel={listLabel}
        handleLabel={(it) => t('reorder.handle', { name: getName(it) })}
        renderItem={(it) => {
          const after = trailing?.(it);
          return (
            <div className="flex min-w-0 items-center justify-between gap-3">
              <span className="min-w-0 truncate font-medium text-[var(--color-text-primary)]">{getName(it)}</span>
              {after && <span className="flex shrink-0 items-center gap-2">{after}</span>}
            </div>
          );
        }}
      />
    </StagedDrawer>
  );
}

export interface ReorderVerb {
  /** The list page's ⋯ entry (`TabbedListShell`'s `reorderAction`) — absent unless the viewer may and two or more stand to be ordered. */
  listAction: (count: number, allowed: boolean) => ListAction | undefined;
  /** The "two or more" rule — an entity registry's hook reads it into the fact its row shows on (the areas'). */
  offered: (count: number, allowed: boolean) => boolean;
  /** Open the drawer — an entity registry's row runs it (the row says `common:reorder.action`, the same word). */
  start: () => void;
  /** Spread onto the `ReorderDrawer`. */
  drawer: { open: boolean; onClose: () => void };
}

/** The Reorder verb (ADR-046) — one open state, one label, one "two or more" rule, whichever door offers it. */
export function useReorderVerb(): ReorderVerb {
  const { t } = useTranslation('common');
  const [open, setOpen] = useState(false);
  const label = t('reorder.action');
  const start = () => setOpen(true);
  const offered = (count: number, allowed: boolean) => allowed && count >= 2;
  return {
    listAction: (count, allowed) =>
      offered(count, allowed) ? { id: 'reorder', label, icon: <icons.drag size={16} aria-hidden />, onAction: start, group: 'reorder' } : undefined,
    offered,
    start,
    drawer: { open, onClose: () => setOpen(false) },
  };
}
